import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/api/fetch";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { isStaff } from "@/features/auth/utils/staff";
import { fetchEvidenceStats } from "../api/claimHunter";
import type { EvidenceCounts, EvidenceStats } from "../types";
import { CSS as PAGE_CSS } from "./ClaimHunterPage";

const WINDOWS = [7, 30, 90] as const;

const STATS_CSS = `
.ev-wrap { max-width: 960px; }
.ev-controls { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin: 22px 0 0; }
.ev-controls .ch-chips { margin: 0; }
.ev-figs { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid var(--ch-ink); margin-top: 26px; }
@media (max-width: 720px) { .ev-figs { grid-template-columns: 1fr 1fr; } }
.ev-chart + .ev-chart { margin-top: 16px; }
.ev-fig { padding: 14px 16px 16px 0; border-bottom: 1px solid var(--ch-rule); }
.ev-fig b { display: block; font-size: 30px; font-weight: 400; line-height: 1.1; font-variant-numeric: tabular-nums; }
.ev-fig span { display: block; font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--ch-ink3); margin-top: 4px; }
.ev-fig small { display: block; font-size: 12px; font-style: italic; color: var(--ch-ink2); margin-top: 3px; }
.ev-h { font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ch-ink3); font-weight: 400; margin: 44px 0 8px; }
.ev-table { width: 100%; border-collapse: collapse; font-size: 13px; font-variant-numeric: tabular-nums; }
.ev-table th { font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ch-ink3); font-weight: 400; text-align: right; padding: 6px 0 6px 12px; border-bottom: 1px solid var(--ch-ink); }
.ev-table td { text-align: right; padding: 5px 0 5px 12px; border-bottom: 1px solid var(--ch-rule); }
.ev-table th:first-child, .ev-table td:first-child { text-align: left; padding-left: 0; }
.ev-table td.zero { color: var(--ch-rule2); }
.ev-table code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.ev-scroll { max-height: 420px; overflow-y: auto; }
.ev-table .ev-q { text-align: left; }
.ev-table td.ev-q { line-height: 1.4; }
.ev-table td.ev-q a { color: var(--ch-ink); text-underline-offset: 3px; }
.ev-table td.ev-when { white-space: nowrap; color: var(--ch-ink2); }
.ev-table td.ev-err { color: var(--ch-against); }
.ev-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
@media (max-width: 720px) { .ev-pair { grid-template-columns: 1fr; gap: 0; } }
.ev-dist { display: grid; grid-template-columns: 60px 1fr 70px; align-items: center; gap: 6px 12px; font-size: 13px; font-variant-numeric: tabular-nums; }
.ev-dist i { display: block; height: 10px; background: var(--ch-ink2); }
.ev-dist .n { text-align: right; color: var(--ch-ink2); }
.ev-dist.ev-src { grid-template-columns: 120px 1fr 140px; }
.ev-dist.ev-src i.api { background: var(--ch-ink); }
.ev-note { font-size: 12.5px; color: var(--ch-ink2); margin: 10px 0 0; }
.ev-defs { margin-top: 48px; font-size: 12.5px; color: var(--ch-ink2); }
.ev-defs dt { font-style: italic; color: var(--ch-ink); margin-top: 10px; }
.ev-defs dd { margin: 2px 0 0; }
`;

type Series = {
  key: keyof EvidenceCounts;
  label: string;
  color: string;
  /** Part of `key` drawn in its own color at the bottom of each bar. */
  part?: { key: keyof EvidenceCounts; label: string; color: string };
};

const SERIES: Series[] = [
  { key: "visits", label: "Visits", color: "var(--ch-ink)" },
  {
    key: "queries",
    label: "Queries",
    color: "var(--ch-answer)",
    part: { key: "queries_api_key", label: "API key", color: "var(--ch-ink)" },
  },
  { key: "signups", label: "Attributed signups", color: "var(--ch-for)" },
];

const fmt = (n: number | null | undefined, digits = 0): string =>
  n == null ? "—" : n.toLocaleString("en-US", { maximumFractionDigits: digits });

const pct = (n: number | null): string => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);

const shortDate = (iso: string): string => {
  const [, m, d] = iso.split("-").map(Number);
  return `${m}/${d}`;
};

const when = (iso: string, timeZone: string): string =>
  new Date(iso).toLocaleString("en-US", {
    timeZone,
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** One small bar chart per series, each on its own scale so signups stay visible next to visits. */
function DailyChart({ daily }: { daily: EvidenceStats["daily"] }) {
  const W = 900;
  const H = 70;
  const left = 34;
  const step = (W - left) / Math.max(daily.length, 1);
  const bar = Math.max(1, step * 0.7);
  const tickEvery = Math.ceil(daily.length / 10);
  return (
    <div className="ch-plot">
      {SERIES.map((s) => {
        const values = daily.map((d) => d[s.key]);
        const max = Math.max(1, ...values);
        return (
          <div key={s.key} className="ev-chart">
            <div className="ch-plot-title">
              {s.label} · peak {fmt(Math.max(0, ...values))}/day
              {s.part ? (
                <>
                  {" "}
                  · <span style={{ color: s.part.color }}>■</span> {s.part.label}{" "}
                  <span style={{ color: s.color }}>■</span> web
                </>
              ) : null}
            </div>
            <svg viewBox={`0 0 ${W} ${H + 14}`} role="img" aria-label={`${s.label} per day`}>
              <line x1={left} x2={W} y1={H} y2={H} stroke="var(--ch-rule2)" />
              <text x={left - 6} y={8} textAnchor="end">
                {fmt(max)}
              </text>
              <text x={left - 6} y={H} textAnchor="end">
                0
              </text>
              {values.map((v, i) => {
                const h = (v / max) * (H - 4);
                const x = left + i * step + (step - bar) / 2;
                const p = s.part ? daily[i][s.part.key] : 0;
                const ph = (p / max) * (H - 4);
                const tip = s.part
                  ? `${daily[i].date}: ${fmt(v)} (${fmt(v - p)} web, ${fmt(p)} ${s.part.label})`
                  : `${daily[i].date}: ${fmt(v)}`;
                return (
                  <g key={daily[i].date}>
                    <title>{tip}</title>
                    <rect x={x} y={H - h} width={bar} height={h - ph} fill={s.color} />
                    {s.part && ph > 0 ? (
                      <rect x={x} y={H - ph} width={bar} height={ph} fill={s.part.color} />
                    ) : null}
                  </g>
                );
              })}
              {daily.map((d, i) =>
                i % tickEvery === 0 ? (
                  <text key={d.date} x={left + i * step + step / 2} y={H + 12} textAnchor="middle">
                    {shortDate(d.date)}
                  </text>
                ) : null,
              )}
            </svg>
          </div>
        );
      })}
    </div>
  );
}

function Cell({ n }: { n: number }) {
  return <td className={n ? undefined : "zero"}>{fmt(n)}</td>;
}

function Stats({ stats }: { stats: EvidenceStats }) {
  const t = stats.totals;
  const perIp = stats.queries_per_ip;
  const bucketMax = Math.max(1, ...perIp.buckets.map((b) => b.ips));
  const webQueries = t.queries_anonymous + t.queries_web;
  const share = (n: number) => (t.queries ? pct(n / t.queries) : "—");
  const sources = [
    { label: "Web, logged out", n: t.queries_anonymous, api: false },
    { label: "Web, signed in", n: t.queries_web, api: false },
    { label: "API key", n: t.queries_api_key, api: true },
  ];
  const sourceMax = Math.max(1, ...sources.map((s) => s.n));
  const recent = stats.recent_queries ?? [];
  return (
    <>
      <div className="ev-figs">
        <div className="ev-fig">
          <b>{fmt(t.visits)}</b>
          <span>Page visits</span>
          <small>{fmt(t.unique_visitor_ips)} unique IPs</small>
        </div>
        <div className="ev-fig">
          <b>{fmt(t.queries)}</b>
          <span>Queries</span>
          <small>
            {fmt(webQueries)} web · {fmt(t.queries_api_key)} API ({share(t.queries_api_key)})
          </small>
        </div>
        <div className="ev-fig">
          <b>{fmt(t.unique_query_ips)}</b>
          <span>Unique querying IPs</span>
          <small>
            {fmt(perIp.mean, 2)} mean · {fmt(perIp.median, 1)} median per IP
          </small>
        </div>
        <div className="ev-fig">
          <b>{pct(t.visit_to_query_conversion)}</b>
          <span>Visit → query</span>
          <small>
            {fmt(t.converted_visitor_ips)} of {fmt(t.unique_visitor_ips)} visitor IPs queried
          </small>
        </div>
        <div className="ev-fig">
          <b>{fmt(t.signups)}</b>
          <span>Attributed signups</span>
          <small>new account within 7 days of a logged-out visit</small>
        </div>
        <div className="ev-fig">
          <b>{fmt(t.new_accounts_queried_24h)}</b>
          <span>New accounts that queried within 24h</span>
          <small>signed-in Evidence query in first 24h</small>
        </div>
      </div>

      <h2 className="ev-h">Queries by source</h2>
      <p className="ev-note">
        Web {fmt(webQueries)} ({share(webQueries)}) · API {fmt(t.queries_api_key)} (
        {share(t.queries_api_key)})
      </p>
      <div className="ev-dist ev-src" style={{ marginTop: 12 }}>
        {sources.map((s) => (
          <div key={s.label} style={{ display: "contents" }}>
            <span>{s.label}</span>
            <i
              className={s.api ? "api" : undefined}
              style={{ width: `${(s.n / sourceMax) * 100}%` }}
            />
            <span className="n">
              {fmt(s.n)} · {share(s.n)}
            </span>
          </div>
        ))}
      </div>

      <h2 className="ev-h">Per day ({stats.timezone})</h2>
      <DailyChart daily={stats.daily} />

      <h2 className="ev-h">Queries per IP</h2>
      <p className="ev-note">
        Mean {fmt(perIp.mean, 2)} · median {fmt(perIp.median, 1)} · p90 {fmt(perIp.p90)} · max{" "}
        {fmt(perIp.max)}
      </p>
      <div className="ev-dist" style={{ marginTop: 12 }}>
        {perIp.buckets.map((b) => (
          <div key={b.label} style={{ display: "contents" }}>
            <span>{b.label}</span>
            <i style={{ width: `${(b.ips / bucketMax) * 100}%` }} />
            <span className="n">{fmt(b.ips)} IPs</span>
          </div>
        ))}
      </div>

      <div className="ev-pair">
        <div>
          <h2 className="ev-h">Top IPs by queries</h2>
          <table className="ev-table">
            <thead>
              <tr>
                <th>IP hash</th>
                <th>Queries</th>
                <th>Signed in</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_ips.map((ip) => (
                <tr key={ip.ip_hash}>
                  <td>
                    <code>{ip.ip_hash}</code>
                  </td>
                  <td>{fmt(ip.queries)}</td>
                  <td>{ip.signed_in ? "yes" : "—"}</td>
                </tr>
              ))}
              {stats.top_ips.length === 0 ? (
                <tr>
                  <td colSpan={3}>No queries yet</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div>
          <h2 className="ev-h">Top referrers</h2>
          <table className="ev-table">
            <thead>
              <tr>
                <th>Host</th>
                <th>Visits</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_referrers.map((r) => (
                <tr key={r.host}>
                  <td>{r.host}</td>
                  <td>{fmt(r.visits)}</td>
                </tr>
              ))}
              {stats.top_referrers.length === 0 ? (
                <tr>
                  <td colSpan={2}>No visits yet</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="ev-h">Recent queries</h2>
      <p className="ev-note">
        The newest {fmt(recent.length)} logged page queries in the window. Queries a signed-in user
        opted out of logging, and API key calls, are never stored.
      </p>
      <div className="ev-scroll" style={{ marginTop: 12 }}>
        <table className="ev-table">
          <thead>
            <tr>
              <th>When</th>
              <th className="ev-q">Query</th>
              <th className="ev-q">Who</th>
              <th>Supports</th>
              <th>Refutes</th>
              <th>Seconds</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((q, i) => (
              <tr key={`${q.at}-${i}`}>
                <td className="ev-when">{when(q.at, stats.timezone)}</td>
                <td className="ev-q">
                  {q.permalink ? (
                    <Link to={`/evidence-api/r/${q.permalink}`}>{q.claim}</Link>
                  ) : (
                    q.claim
                  )}
                </td>
                <td className="ev-q">{q.email ?? (q.signed_in ? "signed in" : "logged out")}</td>
                {q.status === "ok" ? (
                  <>
                    <td>{fmt(q.supports)}</td>
                    <td>{fmt(q.refutes)}</td>
                  </>
                ) : (
                  <td className="ev-err" colSpan={2} title={q.error ?? undefined}>
                    failed
                  </td>
                )}
                <td>{q.latency_ms == null ? "—" : (q.latency_ms / 1000).toFixed(1)}</td>
              </tr>
            ))}
            {recent.length === 0 ? (
              <tr>
                <td colSpan={6}>No logged queries yet</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h2 className="ev-h">Daily table</h2>
      <div className="ev-scroll">
        <table className="ev-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Visits</th>
              <th>Visitor IPs</th>
              <th>Queries</th>
              <th>Web anon</th>
              <th>Web signed in</th>
              <th>API</th>
              <th>Query IPs</th>
              <th>Signups</th>
              <th>Queried ≤24h</th>
            </tr>
          </thead>
          <tbody>
            {[...stats.daily].reverse().map((d) => (
              <tr key={d.date}>
                <td>{d.date}</td>
                <Cell n={d.visits} />
                <Cell n={d.unique_visitor_ips} />
                <Cell n={d.queries} />
                <Cell n={d.queries_anonymous} />
                <Cell n={d.queries_web} />
                <Cell n={d.queries_api_key} />
                <Cell n={d.unique_query_ips} />
                <Cell n={d.signups} />
                <Cell n={d.new_accounts_queried_24h} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="ev-defs">
        <dt>Visits</dt>
        <dd>
          One per load of /evidence-api in a browser running JavaScript. Unique counts use a salted
          hash of the IP address, so people behind one network share an IP and one person on two
          networks counts twice.
        </dd>
        <dt>Queries</dt>
        <dd>
          Successful /claims/support calls: logged-out page queries, signed-in page queries, and API
          key calls. Failed queries are not counted. Web means the Evidence API page (logged out, or
          signed in with a browser session); API means a call authenticated with a Paperclip API
          key.
        </dd>
        <dt>Visit → query</dt>
        <dd>
          Visitor IPs that also ran a page query (anonymous or signed in) in the window, over all
          visitor IPs. API key traffic is left out.
        </dd>
        <dt>Attributed signups</dt>
        <dd>
          New accounts created in the same browser within 7 days of viewing the page logged out.
          “New accounts that queried within 24h” is a server-side cross-check that needs no
          browser state.
        </dd>
        <dt>Internal</dt>
        <dd>
          With internal off, events by @gxl.ai accounts are dropped. Logged-out events from staff
          can’t be told apart and are always included.
        </dd>
      </dl>
    </>
  );
}

export function EvidenceStatsPage() {
  const { user, loading } = useAuth();
  const [days, setDays] = useState<number>(30);
  const [includeInternal, setIncludeInternal] = useState(false);
  const staff = isStaff(user?.email);
  const query = useQuery({
    queryKey: ["evidence-stats", days, includeInternal],
    queryFn: () => fetchEvidenceStats(days, includeInternal),
    enabled: staff,
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });
  const forbidden =
    (!loading && !staff) || (query.error instanceof ApiError && query.error.status === 403);

  return (
    <div className="ch-root">
      <style>{PAGE_CSS + STATS_CSS}</style>
      <div className="ch-wrap ev-wrap">
        <header>
          <h1 className="ch-kicker">GXL Paperclip · Evidence API · Usage</h1>
          <p className="ch-lede">
            Who uses the <Link to="/evidence-api">Evidence API page</Link>, last {days} days.
          </p>
        </header>
        {forbidden ? (
          <div className="ch-status">403 · This page is for GXL staff only.</div>
        ) : (
          <>
            <div className="ev-controls">
              <div className="ch-chips" role="group" aria-label="Window">
                {WINDOWS.map((w) => (
                  <button
                    key={w}
                    type="button"
                    className={`ch-chip${w === days ? " on" : ""}`}
                    aria-pressed={w === days}
                    onClick={() => setDays(w)}
                  >
                    {w} days
                  </button>
                ))}
              </div>
              <label className="ch-toggle">
                <input
                  type="checkbox"
                  checked={includeInternal}
                  onChange={(e) => setIncludeInternal(e.target.checked)}
                />
                Include @gxl.ai users
              </label>
            </div>
            {query.isPending ? <div className="ch-status">Counting…</div> : null}
            {query.error && !forbidden ? (
              <div className="ch-status">{query.error.message}</div>
            ) : null}
            {query.data ? <Stats stats={query.data} /> : null}
          </>
        )}
      </div>
    </div>
  );
}
